import React, { useState } from 'react';
import PropTypes from 'prop-types';
import SwipeableViews from 'react-swipeable-views';

import SwipeableTrayTrigger from './SwipeableTrayTrigger';
import VehicleCard from './VehicleCard';
import HeightUpdater from './HeightUpdater';

const SwipeableTray = ({
	bodyStyle,
	children,
	defaultOpen = false,
	fullScreen = false,
	marginTop = 0,
	onChange,
	overflowHeight = 0,
	overlay = true,
	overlayStyle,
	shadowTip = true,
	style,
	swipeableViewsProps = {},
	topShadow = true,
	currentStep
}) => {
	const [windowHeight, setHeight] = useState(window.innerHeight);
	const [open, setOpen] = useState(defaultOpen);

	const onHeightChange = (newHeight) => {
		setHeight(newHeight);
	};

	const onChangeIndex = (index) => {
		setOpen(index === 1);
		if (onChange !== undefined) {
			onChange(open);
		}
		if (index === 0) {
			document.getElementById('sv-children-container').scrollTop = 0;
		}
	};

	const onTransitionEnd = () => {
		if (swipeableViewsProps.onTransitionEnd) {
			swipeableViewsProps.onTransitionEnd();
		}
	};

	const hideShadows = false;
	const index = open ? 1 : 0;
	const bodyMaxHeight = windowHeight - marginTop - 25;

	const styles = {
		root: {
			height: overflowHeight,
			position: 'fixed',
			bottom: 0,
			right: 0,
			left: 0,
			zIndex: 1,
			...style
		},
		swiper: {
			root: {
				overflowY: 'initial',
				boxSizing: 'border-box',
				...swipeableViewsProps.style
			},
			container: {
				boxSizing: 'border-box',
				...(topShadow &&
					!hideShadows && {
						boxShadow: 'rgba(0, 0, 0, 0.156863) 0px -6px 5px'
					}),
				...swipeableViewsProps.containerStyle
			},
			slide: {
				boxSizing: 'border-box',
				overflow: 'visible',
				marginBottom: -overflowHeight,
				...swipeableViewsProps.slideStyle
			},
			bottomSlide: { marginBottom: overflowHeight },
			body: {
				overflow: open ? 'auto' : 'hidden',
				backgroundColor: 'white',
				height: fullScreen ? bodyMaxHeight : 'initial',
				maxHeight: bodyMaxHeight,
				...bodyStyle
			}
		},
		overlay: {
			position: 'fixed',
			top: 0,
			right: 0,
			left: 0,
			height: windowHeight,
			transition: 'opacity 450ms',
			pointerEvents: 'none',
			backgroundColor: 'black',
			opacity: 0,
			...(open && { opacity: 0.54, pointerEvents: 'auto' }),
			...overlayStyle
		},
		shadowTip: {
			position: 'fixed',
			height: 60,
			width: '200%',
			bottom: -60,
			left: '-50%',
			boxShadow: 'rgba(0, 0, 0, 0.7) 0px 0px 30px',
			transition: 'transform 450ms',
			transform: open ? 'translateY(50px)' : 'translateY(0)'
		}
	};

	return (
		<div data-testid="swipeable-tray-root" style={styles.root}>
			<HeightUpdater
				height={windowHeight}
				onHeightChange={onHeightChange}
			/>
			{overlay && (
				<div
					data-testid="overlay"
					style={styles.overlay}
					onClick={() => onChangeIndex(0)}
					role="presentation"
					aria-hidden="true"
				/>
			)}
			<SwipeableViews
				data-testid="swipeable-views-container"
				index={index}
				axis="y"
				enableMouseEvents
				onChangeIndex={onChangeIndex}
				{...swipeableViewsProps}
				onTransitionEnd={onTransitionEnd}
				style={styles.swiper.root}
				containerStyle={styles.swiper.container}
				slideStyle={styles.swiper.slide}
			>
				<div
					id="sv-children-container"
					data-testid="swipeable-tray-wrapper"
					style={styles.swiper.body}
					className={`border-top rounded-top ReactSwipeableTray--${
						open ? 'open' : 'closed'
					}`}
				>
					{children}
					<SwipeableTrayTrigger />
					<VehicleCard
						onChangeIndex={onChangeIndex}
						useTray
						currentStep={currentStep}
					/>
				</div>
				<div style={styles.swiper.bottomSlide} />
			</SwipeableViews>
			{shadowTip && !hideShadows && (
				<div data-testid="shadow-tip" style={styles.shadowTip} />
			)}
		</div>
	);
};

SwipeableTray.propTypes = {
	bodyStyle: PropTypes.shape({}),
	children: PropTypes.node.isRequired,
	defaultOpen: PropTypes.bool,
	fullScreen: PropTypes.bool,
	marginTop: PropTypes.number,
	onChange: PropTypes.func,
	open: PropTypes.bool,
	overflowHeight: PropTypes.number,
	overlay: PropTypes.bool,
	overlayStyle: PropTypes.shape({}),
	shadowTip: PropTypes.bool,
	style: PropTypes.shape({}),
	swipeableViewsProps: PropTypes.shape({}),
	topShadow: PropTypes.bool,
	currentStep: PropTypes.string
};

export default SwipeableTray;
